import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import { getCustomerQuery } from "../../supportFunctions/graphql/CustomerQueries";
import CustomerApplication from "./CustomerApplication";
import CustomerForm, { Mode } from "./CustomerForm";
import * as Permissions from "../../supportFunctions/Permissions";
import { PageHeader } from "@velocity-ehs/component-library";
import { useTranslation } from "react-i18next";
import {
  getStatusIndicatorProps,
  statusEnum,
} from "../../sharedComponents/StatusIndicator";
import UsersService from "@velocity-ehs/user-utils-module";
import VerticalTabs, { TabItem } from "../../sharedComponents/VerticalTabs";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Customer } from "./Customer";
import GatewayService from "../../../services/GatewayService";
import CustomerApplicationsEdit from "./CustomerApplicationsEdit";
import { useForm, UseFormReturn } from "react-hook-form";

const EditCustomer: React.FC<{ editPermission: string[] }> = ({
  editPermission = [],
}) => {
  const { productChangeTracking } = useFlags();
  const history = useHistory();

  const [customer, setCustomer] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const customerDetailMode = Permissions.IsAllowed(
    editPermission,
    UsersService.permissions
  )
    ? Mode.Edit
    : Mode.View;

  const getCustomer = () => {
    setIsLoading(true);
    GatewayService.api
      .post("", getCustomerQuery(id))
      .then((response: any) => {
        try {
          setCustomer(response.data.data.customer);
        } catch {
          setIsLoading(false);
          const error = {
            response: {
              data: { message: t("message_failed_to_get_customer_s_data") },
            },
          };
          attemptShowErrorsInSnackbar(showSnackbar, error);
        }
      })
      .catch((error: any) => {
        attemptShowErrorsInSnackbar(showSnackbar, error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getCustomer();
    // eslint-disable-next-line
  }, []);

  // Use a specific type annotation to avoid circular reference
  const formMethods: UseFormReturn<any, any> = useForm({
    mode: "onBlur",
    defaultValues: {
      name: customer?.name,
      customerDomainName: customer?.customerDomainName,
      velocityEhsCustomerNumber: customer?.velocityEhsCustomerNumber,
      orgHierarchyManagementEnabled: customer?.orgHierarchyManagementEnabled,
      locationManagementEnabled: customer?.locationManagementEnabled,
      fullyIntegrated: customer?.fullyIntegrated,
    },
  });

  const tabs: TabItem[] = [
    {
      title: t("customer_Customer_Detail_Tab"),
      content: (
        <CustomerForm
          mode={customerDetailMode}
          customer={customer}
          isLoading={isLoading}
          onSaveCustomer={getCustomer}
          formMethods={formMethods}
        />
      ),
    },
  ];
  if (
    Permissions.IsAllowed(
      [Permissions.canAssignApplications],
      UsersService.permissions
    )
  ) {
    tabs.push({
      path: "/applicationAssignment",
      title: t("customer_Applications_Tab"),
      content: <CustomerApplicationsEdit customerId={id} />,
    });
  }

  return (
    <>
      <PageHeader
        title={customer ? customer?.name : undefined}
        navigationTree={[
          { text: `Accelerate ${t("universal_Home")}`, href: "/" },
          { text: t("universal_Customers"), href: "/customer" },
          { text: t("action_edit"), href: "" },
        ]}
        primaryButton={{
          loading: formMethods.formState.isSubmitting,
          type: "submit",
          form: "customerForm",
          label: t("universal_Save"),
        }}
        secondaryButton={{
          type: "button",
          label: t("universal_Cancel"),
          className: "p-button-text",
          onClick: () => history.push("/customer"),
        }}
        statusIndicator={
          customer &&
          getStatusIndicatorProps({
            status: customer.active ? statusEnum.Active : statusEnum.Inactive,
          })
        }
      />
      <VerticalTabs
        tabs={tabs}
        includeChangeTracking={productChangeTracking}
        targetIdentifierType="CUSTOMER_APPLICATION"
      />
    </>
  );
};

export default EditCustomer;
